import type { IFluidCodeDetails } from '@fluidframework/core-interfaces';
import type { ComponentLoaderData } from '@fluidx/office-fluid-types';
import type { DriverInfo } from '@ms/office-web-host';
import { observer } from 'mobx-react-lite';
import { logUsage } from 'owa-analytics';
import { assertNever } from 'owa-assert';
import { timestamp } from 'owa-datetime';
import { lazyOperateContent, OperateContentType } from 'owa-editor';
import {
    ComposeLinkParameters,
    lazyProcessFluidPlaceholder,
    onFluidPlaceholderCreated,
    onFluidFileCreationFailed,
} from 'owa-fluid-link';
import { getGuid } from 'owa-guid';
import loc from 'owa-localize';
import * as trace from 'owa-trace';
import React, { useEffect } from 'react';
import * as ReactDOM from 'react-dom';
import { PositionType } from 'roosterjs-editor-types';
import { fluidPlaceholderText } from './FluidPlaceholder.locstring.json';
import { getFluidLink } from '../selectors/getFluidLink';
import type { FluidLinkViewState } from '../store/schema/FluidLinkViewState';
import {
    isFluidContainerConfigForEditor,
    isFluidContainerConfigForDiv,
    FluidContainerConfig,
} from '../store/schema/FluidContainerConfig';
import { getDriverInfoForNewFile } from '../utils/getDriverInfoForNewFile';
import {
    FluidOwaSource,
    FluidOwaScenario,
    FluidLoadOwaScenario,
    FluidLoadSource,
} from 'owa-fluid-validations';
import { mountNewFluidComponent } from '../utils/mountComponent/mountNewFluidComponent';
import { lazyGetScenarioStartTime } from 'owa-fluid-perf-datapoints';
import { fluidComponentError } from 'owa-fluid-datapoints';

interface FluidPlaceholderProps {
    containerConfig: FluidContainerConfig;
    composeLinkParameters: ComposeLinkParameters;
    driverInfo: DriverInfo;
    codeDetails: IFluidCodeDetails;
    heroComponentConfiguration: ComponentLoaderData | undefined;
    linkId: string;
    operateContent: OperateContentType;
    targetWindow: Window;
    scenarioStartTime: number;
    owaSource: FluidOwaSource;
    addRecipientFunc: (address: string) => void;
    viewStateId?: string;
}

const styles = require('./FluidPlaceholder.scss');
const classNames = require('classnames');

const FluidPlaceholder = observer(function FluidPlaceHolder(props: FluidPlaceholderProps) {
    const fluidPlaceholderRef: React.RefObject<HTMLDivElement> = React.createRef();

    const containerConfig = props.containerConfig;

    let editorViewState, containerDiv;
    if (isFluidContainerConfigForDiv(containerConfig)) {
        containerDiv = containerConfig.containerDiv;
    } else if (isFluidContainerConfigForEditor(containerConfig)) {
        editorViewState = containerConfig.editorViewState;
    }

    // After we successfully retrieve sharing link, we will replace this with beautiful link and remove the view state.
    const viewState: FluidLinkViewState | undefined = getFluidLink(containerConfig.containerId);

    async function processPlaceholder() {
        const proccessFluidPlaceholder = await lazyProcessFluidPlaceholder.import();

        // By this point we know the placeholder is in the DOM.
        // We use the linkId not the containerId, since processFluidPlaceholder
        // will replace the element with whatever Id we find with the beautiful link.
        proccessFluidPlaceholder(
            editorViewState,
            viewState!.url,
            props.linkId,
            containerConfig.containerId,
            props.composeLinkParameters,
            props.targetWindow,
            props.owaSource,
            props.viewStateId,
            containerDiv
        );
    }

    useEffect(() => {
        if (viewState?.url && !viewState?.isRetrivingLink) {
            processPlaceholder();
        }
    }, [viewState?.url, viewState?.isRetrivingLink]);

    useEffect(() => {
        // Mount event
        const fluidPlaceholder: HTMLSpanElement = fluidPlaceholderRef.current!;

        if (fluidPlaceholder) {
            fluidPlaceholder.setAttribute('contenteditable', 'false');
            const mount = () => {
                const scenario = getScenarioFromSource(props.owaSource);
                mountNewFluidComponent(
                    containerConfig,
                    fluidPlaceholder,
                    props.driverInfo,
                    props.codeDetails,
                    props.scenarioStartTime,
                    props.owaSource,
                    scenario,
                    props.addRecipientFunc,
                    props.heroComponentConfiguration,
                    props.viewStateId
                );
            };

            if (isFluidContainerConfigForDiv(containerConfig)) {
                mount();
            } else if (isFluidContainerConfigForEditor(containerConfig)) {
                props.operateContent(editorViewState, (editor, range, domUtils) => {
                    // We first insert the placeholder for the link
                    editor.insertNode(fluidPlaceholder);
                    const spaceNode = editor.getDocument().createTextNode('\u00A0');
                    editor.insertNode(spaceNode);

                    containerConfig.editor = editor;

                    // Then we insert the iframe to create the new component
                    mount();
                    return domUtils.createRange(fluidPlaceholder, PositionType.After);
                });
            }
        }
    }, []);

    return (
        <div
            ref={fluidPlaceholderRef}
            id={props.linkId}
            className={classNames(
                styles.OWA_INSERT_LINK_PLACEHOLDER_CLASSNAME,
                styles.placeholder
            )}>
            {<React.Fragment>{loc(fluidPlaceholderText)}</React.Fragment>}
        </div>
    );
});

export async function createNewFluidComponent(
    containerConfig: FluidContainerConfig,
    codeDetails: IFluidCodeDetails,
    heroComponentConfiguration: ComponentLoaderData | undefined,
    componentType: string,
    composeLinkParameters: ComposeLinkParameters,
    subject: string | undefined,
    targetWindow: Window,
    owaSource: FluidOwaSource,
    addRecipientFunc: (address: string) => void,
    viewStateId?: string
) {
    const chapterTwoScenarioStartTime = await lazyGetScenarioStartTime.importAndExecute();
    const scenarioStartTime = chapterTwoScenarioStartTime
        ? chapterTwoScenarioStartTime
        : timestamp();

    logUsage(
        'CreateNewFluidComponent',
        {
            owaSource: owaSource,
            component: componentType,
        },
        { isCore: true }
    );

    const linkId = 'fluidLink' + getGuid();
    onFluidPlaceholderCreated(containerConfig.containerId, owaSource, viewStateId);
    const document = (targetWindow || window).document;
    const anchorContainer = document.createElement('span');

    let driverInfo;
    try {
        driverInfo = await getDriverInfoForNewFile(codeDetails, owaSource, subject);
    } catch (error) {
        fluidComponentError(
            FluidLoadOwaScenario.ComposeWithNewComponent,
            owaSource,
            FluidLoadSource.Bohemia,
            error
        );

        onFluidFileCreationFailed(containerConfig.containerId, owaSource, viewStateId);
        return;
    }

    // use operate content to insert the div into the editor.
    const operateContent = await lazyOperateContent.import();
    ReactDOM.render(
        <React.StrictMode>
            <FluidPlaceholder
                containerConfig={containerConfig}
                composeLinkParameters={composeLinkParameters}
                driverInfo={driverInfo}
                codeDetails={codeDetails}
                heroComponentConfiguration={heroComponentConfiguration}
                linkId={linkId}
                operateContent={operateContent}
                targetWindow={targetWindow}
                scenarioStartTime={scenarioStartTime}
                owaSource={owaSource}
                addRecipientFunc={addRecipientFunc}
                viewStateId={viewStateId}
            />
        </React.StrictMode>,
        anchorContainer
    );
}

function getScenarioFromSource(source: FluidOwaSource): FluidOwaScenario {
    switch (source) {
        case FluidOwaSource.MailCompose:
            return FluidOwaScenario.MAIL_CREATE_NEW;
        case FluidOwaSource.AgendaEditor:
        case FluidOwaSource.CalendarCompose:
            return FluidOwaScenario.CALENDAR_CREATE_NEW;
        case FluidOwaSource.MailReadingPane:
        case FluidOwaSource.MailCalendarCard:
        case FluidOwaSource.CalendarReadingPane:
        case FluidOwaSource.Timestream:
        case FluidOwaSource.None:
            trace.errorThatWillCauseAlert(
                `Incompatible fluid source for create new fluid component. Source is ${source}`
            );
            return FluidOwaScenario.UNKNOWN_CREATE_NEW;
        default:
            return assertNever(source);
    }
}
